import { Theme } from '@repo/shared/types/dashboard';
import { type RfEmbedSdkConfig, type RfEmbedSdkConfigInput, type RfEmbedSdkConfigLegacyInput, type RfEmbedSdkConfigLegacy, type RfEmbedSdkDisplayConfig, type RfEmbedSdkFloatingLauncherConfig, RfEmbedSdkAnchorPosition, RfEmbedSdkDisplayType, RfEmbedSdkInlineTriggerVariant, RfEmbedSdkLegacyDisplayType, RfEmbedSdkLegacyModalPosition, RfEmbedSdkSearchActionType, type RfEmbedSdkLegacySearchDisplay } from '@repo/shared/types/core/widget';
import { logger } from '@repo/shared/utils/core/logger';

export const isRFWidgetLegacyConfig = (value: unknown): value is RfEmbedSdkConfigLegacy => {
	return (typeof value === 'object' && value !== null && 'apiKey' in value && typeof (value as RfEmbedSdkConfigLegacy)?.apiKey === 'string' && ((value as RfEmbedSdkConfigLegacy)?.apiKey?.length ?? 0) > 0);
};

export const translateRFWidgetLegacyConfig = (config: RfEmbedSdkConfigLegacy): RfEmbedSdkConfig => {
	void logger.warn('[Rapidflare] Legacy RFCopilotConfig detected. Please migrate to the new widget format. See https://docs.rapidflare.ai/deploying/web');
	const floatingDisplay: RfEmbedSdkFloatingLauncherConfig = {
		type: RfEmbedSdkDisplayType.FLOATING_LAUNCHER,
		position: (config?.position === RfEmbedSdkAnchorPosition.TOP_LEFT || config?.position === RfEmbedSdkAnchorPosition.TOP_RIGHT || config?.position === RfEmbedSdkAnchorPosition.BOTTOM_LEFT || config?.position === RfEmbedSdkAnchorPosition.BOTTOM_RIGHT) ? config?.position : undefined
	};
	if (typeof config?.widgetTriggerSelector === 'string' && config?.widgetTriggerSelector?.length > 0) {
		floatingDisplay.trigger = config?.widgetTriggerSelector;
	} else if (config?.widgetTriggerSelector instanceof HTMLElement || config?.widgetTriggerSelector instanceof SVGElement) {
		floatingDisplay.trigger = config?.widgetTriggerSelector;
	}
	return {
		apiKey: (typeof config?.apiKey === 'string' && config?.apiKey?.length > 0) ? config?.apiKey : '',
		display: floatingDisplay,
		userId: (typeof config?.userId === 'string' && config?.userId?.length > 0) ? config?.userId : undefined,
		theme: (typeof config?.theme === 'string' && config?.theme?.length > 0 && [Theme.LIGHT, Theme.DARK]?.indexOf?.(config?.theme) > -1) ? config?.theme : undefined,
		onLoad: (typeof config?.onLoad === 'function') ? config?.onLoad : undefined,
		onOpen: (typeof config?.onOpen === 'function') ? config?.onOpen : undefined,
		onClose: (typeof config?.onClose === 'function') ? config?.onClose : undefined
	};
};

export const isRfEmbedSdkLegacyConfig = (config: RfEmbedSdkConfig | RfEmbedSdkConfigLegacy): config is RfEmbedSdkConfigLegacy => {
	return typeof config === 'object' && config !== null && (!('display' in config) || typeof config?.display !== 'object' || Object.keys(config?.display ?? {})?.length === 0);
};

export const translateRfEmbedSdkLegacyConfig = (rfEmbedSdkLegacyConfig: RfEmbedSdkConfigLegacy): NonNullable<RfEmbedSdkConfigLegacyInput['display']> => {
	if (typeof window === 'undefined') {
		throw new Error('[Rapidflare] Cannot translate legacy display mode in server-side environment. This function must be called in a browser environment.');
	}
	let resolvedContainer: string | HTMLElement = '';
	if (typeof rfEmbedSdkLegacyConfig?.container === 'string' && rfEmbedSdkLegacyConfig?.container?.length > 0) {
		resolvedContainer = rfEmbedSdkLegacyConfig?.container;
	} else if (rfEmbedSdkLegacyConfig?.container instanceof HTMLElement) {
		resolvedContainer = rfEmbedSdkLegacyConfig?.container;
	}
	const rfEmbedSdkLegacyAiModeConfig = rfEmbedSdkLegacyConfig?.aiModeConfig;
	if (typeof rfEmbedSdkLegacyAiModeConfig === 'object' && rfEmbedSdkLegacyAiModeConfig != null && Object.keys(rfEmbedSdkLegacyAiModeConfig)?.length > 0) {
		const rfEmbedSdkLegacyShowOverlay = (
			(typeof rfEmbedSdkLegacyAiModeConfig?.showOverlay === 'boolean' && rfEmbedSdkLegacyAiModeConfig?.showOverlay === true) ||
			(typeof rfEmbedSdkLegacyAiModeConfig?.showAiModeOverlay === 'boolean' && rfEmbedSdkLegacyAiModeConfig?.showAiModeOverlay === true)
		);
		if (rfEmbedSdkLegacyShowOverlay) {
			return {
				type: RfEmbedSdkDisplayType.AI_OVERVIEW,
				container: resolvedContainer,
				titleText: (typeof rfEmbedSdkLegacyAiModeConfig?.overlayTitleText === 'string') ? rfEmbedSdkLegacyAiModeConfig?.overlayTitleText : ((typeof rfEmbedSdkLegacyAiModeConfig?.aiModeOverlayTitleText === 'string') ? rfEmbedSdkLegacyAiModeConfig?.aiModeOverlayTitleText : undefined),
				titleLogo: (typeof rfEmbedSdkLegacyAiModeConfig?.overlayTitleLogo === 'string') ? rfEmbedSdkLegacyAiModeConfig?.overlayTitleLogo : ((typeof rfEmbedSdkLegacyAiModeConfig?.aiModeOverlayTitleLogo === 'string') ? rfEmbedSdkLegacyAiModeConfig?.aiModeOverlayTitleLogo : undefined),
				ctaText: (typeof rfEmbedSdkLegacyAiModeConfig?.overlayButtonText === 'string') ? rfEmbedSdkLegacyAiModeConfig?.overlayButtonText : ((typeof rfEmbedSdkLegacyAiModeConfig?.aiModeOverlayButtonText === 'string') ? rfEmbedSdkLegacyAiModeConfig?.aiModeOverlayButtonText : undefined),
				ctaBackground: (typeof rfEmbedSdkLegacyAiModeConfig?.overlayButtonBackground === 'string') ? rfEmbedSdkLegacyAiModeConfig?.overlayButtonBackground : ((typeof rfEmbedSdkLegacyAiModeConfig?.aiModeOverlayButtonBackground === 'string') ? rfEmbedSdkLegacyAiModeConfig?.aiModeOverlayButtonBackground : undefined),
				ctaColor: (typeof rfEmbedSdkLegacyAiModeConfig?.overlayButtonColor === 'string') ? rfEmbedSdkLegacyAiModeConfig?.overlayButtonColor : ((typeof rfEmbedSdkLegacyAiModeConfig?.aiModeOverlayButtonColor === 'string') ? rfEmbedSdkLegacyAiModeConfig?.aiModeOverlayButtonColor : undefined),
				ctaUrl: (typeof rfEmbedSdkLegacyAiModeConfig?.buttonClickUrl === 'string') ? rfEmbedSdkLegacyAiModeConfig?.buttonClickUrl : ((typeof rfEmbedSdkLegacyAiModeConfig?.aiModeButtonClickUrl === 'string') ? rfEmbedSdkLegacyAiModeConfig?.aiModeButtonClickUrl : undefined),
				ctaNewTab: (typeof rfEmbedSdkLegacyAiModeConfig?.buttonClickOpenNewTab === 'boolean') ? rfEmbedSdkLegacyAiModeConfig?.buttonClickOpenNewTab : ((typeof rfEmbedSdkLegacyAiModeConfig?.aiModeButtonClickOpenNewTab === 'boolean') ? rfEmbedSdkLegacyAiModeConfig?.aiModeButtonClickOpenNewTab : undefined)
			};
		}
	}
	const displayModeConfig = rfEmbedSdkLegacyConfig?.displayModeConfig;
	if (typeof displayModeConfig !== 'object' || Object.keys(displayModeConfig ?? {})?.length === 0 || displayModeConfig === null) {
		return {
			type: RfEmbedSdkDisplayType.FULL_PAGE,
			container: resolvedContainer
		};
	}
	const displayType = displayModeConfig?.type;
	if (displayType === RfEmbedSdkLegacyDisplayType.INLINE) {
		return {
			type: RfEmbedSdkDisplayType.FULL_PAGE,
			container: resolvedContainer
		};
	}
	if (displayType === RfEmbedSdkLegacyDisplayType.FLOATING) {
		const rfEmbedSdkLegacyFloatingModeConfig = displayModeConfig?.floatingModeConfig;
		const floatingLauncherDisplay: RfEmbedSdkDisplayConfig = {
			type: RfEmbedSdkDisplayType.FLOATING_LAUNCHER,
			position: rfEmbedSdkLegacyFloatingModeConfig?.position,
			overlay: rfEmbedSdkLegacyFloatingModeConfig?.overlay
		};
		if (typeof rfEmbedSdkLegacyFloatingModeConfig?.widgetTriggerSelector === 'string' && rfEmbedSdkLegacyFloatingModeConfig?.widgetTriggerSelector?.length > 0) {
			floatingLauncherDisplay.trigger = rfEmbedSdkLegacyFloatingModeConfig?.widgetTriggerSelector;
		} else if (rfEmbedSdkLegacyFloatingModeConfig?.widgetTriggerSelector instanceof HTMLElement || rfEmbedSdkLegacyFloatingModeConfig?.widgetTriggerSelector instanceof SVGElement) {
			floatingLauncherDisplay.trigger = rfEmbedSdkLegacyFloatingModeConfig?.widgetTriggerSelector;
		} else if (rfEmbedSdkLegacyFloatingModeConfig?.widgetTriggerElement instanceof HTMLElement || rfEmbedSdkLegacyFloatingModeConfig?.widgetTriggerElement instanceof SVGElement) {
			floatingLauncherDisplay.trigger = rfEmbedSdkLegacyFloatingModeConfig?.widgetTriggerElement;
		}
		if (typeof rfEmbedSdkLegacyFloatingModeConfig?.dragContainerSelector === 'string' && rfEmbedSdkLegacyFloatingModeConfig?.dragContainerSelector?.length > 0) {
			floatingLauncherDisplay.dragContainer = rfEmbedSdkLegacyFloatingModeConfig?.dragContainerSelector;
		}
		return floatingLauncherDisplay;
	}
	if (displayType === RfEmbedSdkLegacyDisplayType.SEARCH) {
		const rfEmbedSdkLegacySearchModeConfig = displayModeConfig?.searchModeConfig;
		return {
			type: RfEmbedSdkDisplayType.SEARCH,
			container: resolvedContainer,
			placeholder: (typeof rfEmbedSdkLegacySearchModeConfig?.placeholder === 'string' && rfEmbedSdkLegacySearchModeConfig?.placeholder?.length > 0) ? rfEmbedSdkLegacySearchModeConfig?.placeholder : undefined,
			minRows: (typeof rfEmbedSdkLegacySearchModeConfig?.minRows === 'number' && rfEmbedSdkLegacySearchModeConfig?.minRows >= 1) ? rfEmbedSdkLegacySearchModeConfig?.minRows : undefined,
			maxRows: (typeof rfEmbedSdkLegacySearchModeConfig?.maxRows === 'number' && rfEmbedSdkLegacySearchModeConfig?.maxRows >= 1) ? rfEmbedSdkLegacySearchModeConfig?.maxRows : undefined,
			autoFocus: (typeof rfEmbedSdkLegacySearchModeConfig?.autoFocus === 'boolean') ? rfEmbedSdkLegacySearchModeConfig?.autoFocus : undefined,
			searchUrl: (typeof rfEmbedSdkLegacySearchModeConfig?.searchClickUrl === 'string') ? rfEmbedSdkLegacySearchModeConfig?.searchClickUrl : undefined,
			searchNewTab: (typeof rfEmbedSdkLegacySearchModeConfig?.searchClickOpenNewTab === 'boolean') ? rfEmbedSdkLegacySearchModeConfig?.searchClickOpenNewTab : undefined,
			aiSuggestionsDisabled: (typeof rfEmbedSdkLegacySearchModeConfig?.aiSuggestionsDisabled === 'boolean') ? rfEmbedSdkLegacySearchModeConfig?.aiSuggestionsDisabled : undefined,
			starterPromptsDisabled: (typeof rfEmbedSdkLegacySearchModeConfig?.starterPromptsDisabled === 'boolean') ? rfEmbedSdkLegacySearchModeConfig?.starterPromptsDisabled : undefined,
			starterPromptHeaderText: (typeof rfEmbedSdkLegacySearchModeConfig?.starterPromptHeaderText === 'string' && rfEmbedSdkLegacySearchModeConfig?.starterPromptHeaderText?.length > 0) ? rfEmbedSdkLegacySearchModeConfig?.starterPromptHeaderText : undefined,
			starterPromptAutoSubmit: (typeof rfEmbedSdkLegacySearchModeConfig?.starterPromptAutoSubmit === 'boolean') ? rfEmbedSdkLegacySearchModeConfig?.starterPromptAutoSubmit : undefined
		};
	}
	if (displayType === RfEmbedSdkLegacyDisplayType.MODAL) {
		const rfEmbedSdkLegacyModalModeConfig = displayModeConfig?.modalModeConfig;
		const rfEmbedSdkLegacyModalPosition = rfEmbedSdkLegacyModalModeConfig?.position;
		const rfEmbedSdkLegacyModalDocked = rfEmbedSdkLegacyModalModeConfig?.docked;
		const resolvedVariant: RfEmbedSdkInlineTriggerVariant = (rfEmbedSdkLegacyModalDocked === true) ? (
			(rfEmbedSdkLegacyModalPosition === RfEmbedSdkLegacyModalPosition.LEFT) ? RfEmbedSdkInlineTriggerVariant.LEFT_DRAWER : RfEmbedSdkInlineTriggerVariant.RIGHT_DRAWER
		) : (
			(rfEmbedSdkLegacyModalPosition === RfEmbedSdkLegacyModalPosition.CENTER) ? RfEmbedSdkInlineTriggerVariant.CENTER_MODAL : (
				(rfEmbedSdkLegacyModalPosition === RfEmbedSdkLegacyModalPosition.LEFT) ? RfEmbedSdkInlineTriggerVariant.LEFT_MODAL : RfEmbedSdkInlineTriggerVariant.RIGHT_MODAL
			)
		);
		let resolvedTrigger: string | HTMLElement = '';
		if (typeof rfEmbedSdkLegacyModalModeConfig?.widgetTriggerSelector === 'string' && rfEmbedSdkLegacyModalModeConfig?.widgetTriggerSelector?.length > 0) {
			resolvedTrigger = rfEmbedSdkLegacyModalModeConfig?.widgetTriggerSelector;
		} else if (rfEmbedSdkLegacyModalModeConfig?.widgetTriggerSelector instanceof HTMLElement) {
			resolvedTrigger = rfEmbedSdkLegacyModalModeConfig?.widgetTriggerSelector;
		}
		return {
			type: RfEmbedSdkDisplayType.INLINE_TRIGGER,
			trigger: resolvedTrigger,
			variant: resolvedVariant,
			overlay: (typeof rfEmbedSdkLegacyModalModeConfig?.overlay === 'boolean') ? rfEmbedSdkLegacyModalModeConfig?.overlay : undefined,
			responsive: (typeof rfEmbedSdkLegacyModalModeConfig?.responsive === 'boolean') ? rfEmbedSdkLegacyModalModeConfig?.responsive : undefined,
			titleText: typeof rfEmbedSdkLegacyModalModeConfig?.title === 'string' && rfEmbedSdkLegacyModalModeConfig?.title?.length > 0 ? rfEmbedSdkLegacyModalModeConfig?.title : undefined,
			titleLogo: typeof rfEmbedSdkLegacyModalModeConfig?.logo === 'string' && rfEmbedSdkLegacyModalModeConfig?.logo?.length > 0 ? rfEmbedSdkLegacyModalModeConfig?.logo : undefined
		};
	}
	return {
		type: RfEmbedSdkDisplayType.FULL_PAGE,
		container: resolvedContainer
	};
};

/**
 * Normalizes a search `display` into the canonical `action` shape. Idempotent and safe to call on
 * any config: non-search displays and search displays that already carry `action` are returned
 * unchanged.
 *
 * - Legacy `searchUrl` present, no `action` → `action.external-url`.
 * - No URL + no `action` → default on-page conversation child: inline-trigger + right-modal.
 */
export const normalizeRfEmbedSdkSearchAction = (config: RfEmbedSdkConfigInput): RfEmbedSdkConfig => {
	const display = config?.display;
	if (display?.type !== RfEmbedSdkDisplayType.SEARCH) {
		return config;
	}
	const searchDisplay = display as RfEmbedSdkLegacySearchDisplay;
	if (typeof searchDisplay?.action === 'object' && searchDisplay?.action !== null) {
		// Already canonical.
		return config;
	}
	// A legacy flat `searchUrl` maps to an external-url action. A lone `searchNewTab` without
	// `searchUrl` does NOT count — it falls through to the default on-page child below.
	if (typeof searchDisplay?.searchUrl === 'string' && searchDisplay?.searchUrl?.trim?.()?.length > 0) {
		return {
			...config,
			display: {
				...searchDisplay,
				action: {
					type: RfEmbedSdkSearchActionType.EXTERNAL_URL,
					url: searchDisplay?.searchUrl,
					newTab: (typeof searchDisplay?.searchNewTab === 'boolean') ? searchDisplay?.searchNewTab : undefined
				}
			}
		};
	}
	// No URL + no action → default on-page conversation child.
	return {
		...config,
		display: {
			...searchDisplay,
			action: {
				type: RfEmbedSdkSearchActionType.WIDGET,
				widget: {
					type: RfEmbedSdkDisplayType.INLINE_TRIGGER,
					variant: RfEmbedSdkInlineTriggerVariant.RIGHT_MODAL
				}
			}
		}
	};
};

/**
 * Normalizes the legacy flat `ambientContext` onto the canonical `context.ambient`. Idempotent:
 * a config without `ambientContext` is returned unchanged, and an explicit `context.ambient` always
 * wins over the legacy field rather than being overwritten by it.
 */
export const normalizeRfEmbedSdkContext = (config: RfEmbedSdkConfigInput): RfEmbedSdkConfig => {
	const { ambientContext, ...canonicalConfig } = config;
	const legacyAmbientContext = (typeof ambientContext === 'string' && ambientContext?.trim?.()?.length > 0) ? ambientContext?.trim?.() : undefined;
	if (legacyAmbientContext === undefined) {
		return canonicalConfig;
	}
	void logger.warn('[Rapidflare] `ambientContext` is deprecated. Please migrate to `context.ambient`. See https://docs.rapidflare.ai/deploying/web');
	return {
		...canonicalConfig,
		context: {
			...canonicalConfig?.context,
			ambient: (typeof canonicalConfig?.context?.ambient === 'string' && canonicalConfig?.context?.ambient?.trim?.()?.length > 0) ? canonicalConfig?.context?.ambient?.trim?.() : legacyAmbientContext
		}
	};
};

export const resolveRfEmbedSdkConfig = (config: RfEmbedSdkConfigInput | RfEmbedSdkConfigLegacy): RfEmbedSdkConfig | null => {
	// The public `init()` passes this straight through from the host page, so it can be null, undefined
	// or a primitive. Both callers already treat null as an invalid config, so return it rather than
	// letting the checks below throw out of the SDK and into the customer's page.
	if (typeof config !== 'object' || config === null) {
		return null;
	}
	if (!isRfEmbedSdkLegacyConfig(config)) {
		return normalizeRfEmbedSdkContext(normalizeRfEmbedSdkSearchAction(config as RfEmbedSdkConfigInput));
	}
	if (isRfEmbedSdkLegacyConfig(config)) {
		void logger.warn('[Rapidflare] Legacy config detected. Please migrate to the new display config format. See https://docs.rapidflare.ai/deploying/web');
		const legacy = config;
		const display = translateRfEmbedSdkLegacyConfig(legacy);
		return normalizeRfEmbedSdkContext(normalizeRfEmbedSdkSearchAction({
			apiKey: (typeof legacy?.apiKey === 'string' && legacy?.apiKey?.length > 0) ? legacy.apiKey : '',
			display,
			userId: (typeof legacy?.userId === 'string' && legacy?.userId?.length > 0) ? legacy.userId : undefined,
			conversationId: typeof legacy?.conversationId === 'string' && legacy?.conversationId?.length > 0 ? legacy.conversationId : undefined,
			theme: (typeof legacy?.theme === 'string' && legacy?.theme?.length > 0 && [Theme.LIGHT, Theme.DARK]?.indexOf?.(legacy?.theme) > -1) ? legacy?.theme : undefined,
			prompt: typeof legacy?.prompt === 'string' && legacy?.prompt?.length > 0 ? legacy.prompt : undefined,
			autoSubmit: (typeof legacy?.autoSubmit === 'boolean') ? legacy?.autoSubmit : undefined,
			onLoad: (typeof legacy?.onLoad === 'function') ? legacy?.onLoad : undefined,
			onOpen: (typeof legacy?.onOpen === 'function') ? legacy?.onOpen : undefined,
			onClose: (typeof legacy?.onClose === 'function') ? legacy?.onClose : undefined
		}));
	}
	return config;
};
